/* eslint  react/jsx-no-bind: 0 */
import React, { useEffect, useMemo, useState } from "react";
import { Route, Routes } from "react-router-dom";
import {
  Box,
  Container,
  CssBaseline,
  IconButton,
  ThemeProvider,
  Tooltip,
  Typography,
  AppBar,
  Toolbar,
  createTheme,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useAuth } from "@shadowhealth/oidc-login";
import PrivateRoute from "./components/PrivateRoute";
import RouterLinkButton from "./components/RouterLinkButton";
import LoginPage from "./pages/LoginPage";
import ScriptsPage from "./pages/ScriptsPage";
import ScriptPage from "./pages/ScriptPage";
import ConvosPage from "./pages/ConvosPage";
import BackgroundJobsPage from "./pages/BackgroundJobsPage";
import EvaluationsPage from "./pages/EvaluationsPage";
import EvaluationDetailPage from "./pages/EvaluationDetailPage";
import EvaluationComparePage from "./pages/EvaluationComparePage";

const MODE_KEY = "convovec-color-mode";

// Start from the OS preference, then remember an explicit choice. Reading
// localStorage lazily keeps the first paint in the right mode.
const initialMode = () => {
  const stored = globalThis.localStorage?.getItem(MODE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const App = () => {
  const auth = useAuth();
  const isLoggedIn = auth?.user;
  const [mode, setMode] = useState(initialMode);

  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);

  useEffect(() => {
    globalThis.localStorage?.setItem(MODE_KEY, mode);
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box component="main" sx={{ minHeight: "100vh" }}>
        <AppBar position="sticky">
          <Toolbar sx={{ gap: 1 }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Shadow Health Conversation Service - ConvoVec [{globalThis.env.ENVIRONMENT}]
            </Typography>
            {isLoggedIn && (
              <>
                <RouterLinkButton to="/" variant="text" color="inherit" primary="Home" />
                <RouterLinkButton to="/scripts" variant="text" color="inherit" primary="Scripts" />
                <RouterLinkButton to="/jobs" variant="text" color="inherit" primary="Background jobs" />
                <RouterLinkButton to="/evaluations" variant="text" color="inherit" primary="Evaluations" />
              </>
            )}
            <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
              <IconButton
                color="inherit"
                aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                onClick={() => setMode((current) => (current === "dark" ? "light" : "dark"))}
              >
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route
              path="/scripts/:scriptName"
              element={
                <PrivateRoute>
                  <ScriptPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/scripts"
              element={
                <PrivateRoute>
                  <ScriptsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/convos/:scriptName/:scriptVersion"
              element={
                <PrivateRoute>
                  <ConvosPage explainEndpoint="/convo/explain" scriptEndpoint="/scripts" />
                </PrivateRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <PrivateRoute>
                  <BackgroundJobsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/evaluations"
              element={
                <PrivateRoute>
                  <EvaluationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/evaluations/compare"
              element={
                <PrivateRoute>
                  <EvaluationComparePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/evaluations/:tag"
              element={
                <PrivateRoute>
                  <EvaluationDetailPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default App;
